import { createSignal } from '@react-rxjs/utils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { FormValues } from '../../../lib/cart/types'
import {
  componentRestrictionsForCountry,
  reducePlaceResultComponent,
  reducersForPlaceResult,
} from '../../../lib/places/reducers'
import { ParsedPlaceResult } from '../../../lib/places/types'
import { setValuesWith } from '../../../utils/formTools'
import AddressForm, { AddressFormProps } from './AddressForm'
import { AutocompleteProps } from './AddressStreetAutocompleteField'

type AddressFormWithAutocompleteProps = Omit<AddressFormProps, 'autocompleteProps'>

// create observable that emits when finished setting values after place selection
const [placeSelectionComplete$, doCompletePlaceSelection] = createSignal()

const AddressFormWithAutocomplete: React.FC<AddressFormWithAutocompleteProps> = ({
  countryOptions,
  regionOptions,
  regionOptionsMap,
  ...otherProps
}) => {
  const [parseSelectedPlaceError, setParseSelectedPlaceError] = useState<string | undefined>(
    undefined,
  )
  const [placeSelection, setPlaceSelection] = useState<ParsedPlaceResult | undefined>(undefined)
  const { setValue, watch } = useFormContext<FormValues.Address>()

  // update fields when place is selected from Google places autocomplete widget
  useEffect(() => {
    if (!placeSelection) {
      return
    }

    const setValues = setValuesWith(setValue)

    const countryListMatch = placeSelection.country
      ? countryOptions.find((country) => country.value === placeSelection.country)
      : undefined
    const countryValue = countryListMatch?.value

    // match on label or code (label seems to be the default response)
    const regionListMatch = placeSelection.region
      ? regionOptionsMap.getByName(placeSelection.region) ||
        regionOptionsMap.getByCode(placeSelection.region)
      : undefined
    const regionValue = regionListMatch?.value || ''

    setValues(
      {
        street1: placeSelection.address1 || '',
        city: placeSelection.city || '',
        ...(countryValue && { country: countryValue }),
        postcode: placeSelection.postalCode || '',
        region: regionValue,
      },
      {
        shouldDirty: true,
        shouldValidate: true,
      },
      doCompletePlaceSelection,
    )
    // onRegionChange(addressComponents.administrative_area_level_1)
  }, [regionOptionsMap, placeSelection, setValue, countryOptions])

  const countryCode = watch('country_code')
  const componentRestrictions = useMemo(
    () => (countryCode ? componentRestrictionsForCountry(countryCode) : undefined),
    [countryCode],
  )

  const onPlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult) => {
      setParseSelectedPlaceError(undefined)

      const addressComponents = place.address_components

      if (!countryCode || !addressComponents) {
        setParseSelectedPlaceError('Unable to populate address fields from selected place.')
        return
      }

      const reducers = reducersForPlaceResult(addressComponents)

      const parsedPlaceResult = addressComponents.reduce(reducePlaceResultComponent(reducers), {})

      // Callback is not executed within context of current render function. Set place
      // selection in state and update field values via `useEffect` instead. This allows
      // access to current state values (e.g. regionOptions).
      setPlaceSelection(parsedPlaceResult)
    },
    [countryCode],
  )

  const autocompleteProps: AutocompleteProps | undefined = useMemo(
    () =>
      componentRestrictions
        ? {
            onPlaceSelected,
            parseSelectedPlaceError,
            placeSelectionComplete$,
          }
        : undefined,
    [componentRestrictions, parseSelectedPlaceError, onPlaceSelected],
  )
  return (
    <AddressForm
      autocompleteProps={autocompleteProps}
      countryOptions={countryOptions}
      regionOptions={regionOptions}
      regionOptionsMap={regionOptionsMap}
      {...otherProps}
    />
  )
}

export default AddressFormWithAutocomplete
